import { Controller } from 'react-hook-form';
import { Col, Row } from 'react-styled-flexboxgrid';
import { useTheme } from 'styled-components';

import {
  BUTTON_TYPES,
  COUNTRY_CODES,
  NOTIFICATION_TYPES,
} from '@savgroup-front-common/constants';
import { Banner } from '@savgroup-front-common/core/atoms/Banners';
import {
  Autocomplete,
  FormGroup,
  Input,
  PlacesAutocomplete,
} from '@savgroup-front-common/core/atoms/Form';
import { FieldMessage } from '@savgroup-front-common/core/atoms/Form/common';
import { FIELD_STATUS } from '@savgroup-front-common/core/atoms/Form/common/helpers/getFinalFieldState.types';
import { PhoneNumberInput } from '@savgroup-front-common/core/atoms/Form/PhoneNumberInput/PhoneNumberInput';
import { SafeFormattedMessageWithoutSpread } from '@savgroup-front-common/core/formatters';
import { getAddress } from '@savgroup-front-common/core/helpers';
import { InputHookForm } from '@savgroup-front-common/core/molecules/Form';
import { InformationIcon } from '@savgroup-front-common/core/protons/icons';

import AgreementCheckbox from './AgreementCheckbox/AgreementCheckbox';
import useOnboardingFormContext from './hooks/useOnboardingFormContext';
import messages from './messages';
import {
  $BannerMessage1,
  $Bold,
  $DomainName,
  $Form,
  $InputRow,
  $MainDomainName,
  $Preview,
  $Row,
  $SubmitButton,
} from './OnboardingForm.styles';

const OnboardingForm = ({
  handleUserEmail,
}: {
  handleUserEmail: (userEmail: string) => void;
}) => {
  const theme = useTheme();
  const {
    handleFormSubmit,
    handleSelectAddress,
    formContext,
    isLoading,
    domain,
    isDomainNameLoading,
    countryOptions,
  } = useOnboardingFormContext({ handleUserEmail });
  const {
    register,
    formState: { errors },
    control,
    handleSubmit,
    watch,
    setValue,
  } = formContext;

  const watchedCountryCode = watch('countryCode');
  const watchedPhoneCountryCode = watch('phoneCountryCode');
  const selectedCountryCode = watchedCountryCode?.value?.toUpperCase();
  const phoneInputCountryCode =
    selectedCountryCode ||
    watchedPhoneCountryCode?.toUpperCase() ||
    COUNTRY_CODES.FR;

  const hasCName = watch('cName');
  const domainNameSplitted = domain?.domainName.split('.');
  const mainDomainName = domainNameSplitted?.[0];
  const residualDomainName = domainNameSplitted?.slice(1).join('.');

  return (
    <$Form onSubmit={handleSubmit(handleFormSubmit)}>
      <$Row>
        <Col xs={12} md={6}>
          <InputHookForm
            name="firstName"
            errors={errors}
            label={messages.firstName}
            register={register}
            isRequired
            dataTestId="OnboardingForm-firstName"
          />
        </Col>
        <Col xs={12} md={6}>
          <InputHookForm
            name="lastName"
            errors={errors}
            label={messages.lastName}
            register={register}
            isRequired
            dataTestId="OnboardingForm-lastName"
          />
        </Col>
      </$Row>
      <$InputRow>
        <InputHookForm
          name="companyName"
          errors={errors}
          label={messages.companyName}
          isRequired
          register={register}
          dataTestId="OnboardingForm-companyName"
        />
      </$InputRow>
      <$InputRow>
        <InputHookForm
          name="cName"
          errors={errors}
          label={messages.cName}
          isRequired
          register={register}
          dataTestId="OnboardingForm-cName"
        />
      </$InputRow>
      {hasCName && (
        <$Preview>
          <SafeFormattedMessageWithoutSpread
            message={messages.preview}
            values={{
              domainName: mainDomainName && (
                <$DomainName>
                  <$MainDomainName>{mainDomainName}</$MainDomainName>
                  {`.${residualDomainName}`}
                </$DomainName>
              ),
            }}
          />
        </$Preview>
      )}
      <FormGroup>
        <Banner
          notificationType={NOTIFICATION_TYPES.DEFAULT}
          isOpen
          isFluid
          icon={
            <InformationIcon
              color={theme.newUI.defaultColors.primary}
              size="24px"
            />
          }
        >
          <div>
            <$Bold>
              <SafeFormattedMessageWithoutSpread
                message={messages.bannerMessageBold1}
              />
            </$Bold>
            &nbsp;
            <$BannerMessage1>
              <SafeFormattedMessageWithoutSpread
                message={messages.bannerMessage1}
              />
            </$BannerMessage1>
          </div>
          <div>
            <SafeFormattedMessageWithoutSpread
              message={messages.bannerMessage2}
            />
          </div>
        </Banner>
      </FormGroup>
      <FormGroup>
        <Controller
          control={control}
          name="address"
          render={({ field }) => {
            return (
              <PlacesAutocomplete
                name={field.name}
                value={field.value}
                label={messages.address}
                errors={errors}
                isRequired
                onSelect={(values) => {
                  handleSelectAddress(values);
                  field.onChange(
                    getAddress(values.street, values.streetNumber),
                  );
                }}
                onChange={field.onChange}
                countryCode={watchedCountryCode?.value}
                dataTestId="OnboardingForm-address"
              />
            );
          }}
        />
      </FormGroup>
      <Row>
        <Col sm={4} xs={12}>
          <FormGroup>
            <Controller
              control={control}
              name="postalCode"
              render={({ field }) => {
                return (
                  <Input
                    name={field.name}
                    value={field.value}
                    label={messages.postalCode}
                    disableBrowserAutocomplete
                    onChange={field.onChange}
                    errors={errors}
                    isRequired
                    dataTestId="OnboardingForm-postalCode"
                  />
                );
              }}
            />
          </FormGroup>
        </Col>
        <Col sm={4} xs={12}>
          <FormGroup>
            <Controller
              control={control}
              name="city"
              render={({ field }) => {
                return (
                  <Input
                    name={field.name}
                    value={field.value}
                    label={messages.city}
                    disableBrowserAutocomplete
                    onChange={field.onChange}
                    errors={errors}
                    isRequired
                    dataTestId="OnboardingForm-city"
                  />
                );
              }}
            />
          </FormGroup>
        </Col>
        <Col sm={4} xs={12}>
          <FormGroup>
            <Controller
              control={control}
              name="countryCode"
              render={({ field }) => {
                return (
                  <Autocomplete
                    name={field.name}
                    value={field.value}
                    label={messages.country}
                    onChange={(option, meta) => {
                      field.onChange(option, meta);

                      const countryCode = option?.value?.toUpperCase();

                      if (!countryCode) {
                        return;
                      }

                      setValue(
                        'phoneCountryCode',
                        countryCode as COUNTRY_CODES,
                        {
                          shouldTouch: false,
                          shouldValidate: false,
                        },
                      );

                      setValue('phone', '');
                    }}
                    options={countryOptions}
                    errors={errors}
                    isRequired
                    dataTestId="OnboardingForm-country"
                  />
                );
              }}
            />
          </FormGroup>
        </Col>
      </Row>
      <$InputRow>
        <InputHookForm
          name="email"
          errors={errors}
          label={messages.email}
          register={register}
          isRequired
          dataTestId="OnboardingForm-email"
        />
      </$InputRow>

      <$InputRow>
        <Controller
          control={control}
          name="phone"
          render={({ field }) => {
            return (
              <PhoneNumberInput
                key={`OnboardingForm-phone-${phoneInputCountryCode}`}
                name={field.name}
                value={field.value}
                label={messages.phone}
                dataTestId="OnboardingForm-phone"
                disableBrowserAutocomplete
                errors={errors}
                defaultCountry={phoneInputCountryCode}
                onBlur={field.onBlur}
                onChange={(
                  newValue: string,
                  meta?: {
                    country: {
                      iso2: string;
                    };
                  },
                ) => {
                  setValue('phone', newValue, {
                    shouldDirty: true,
                    shouldTouch: false,
                    shouldValidate: false,
                  });

                  if (!meta) {
                    return;
                  }

                  const metaCountryCode = meta.country.iso2.toUpperCase();

                  setValue(
                    'phoneCountryCode',
                    metaCountryCode as COUNTRY_CODES,
                    {
                      shouldTouch: false,
                      shouldValidate: false,
                    },
                  );
                }}
                isRequired
              />
            );
          }}
        />
      </$InputRow>

      <$InputRow>
        <Controller
          control={control}
          name="cgv"
          render={({ field }) => {
            return (
              <AgreementCheckbox
                isChecked={field.value}
                onChange={field.onChange}
                isError={!!errors.cgv?.message}
                dataTestId="OnboardingForm-cgv"
              />
            );
          }}
        />
        <FieldMessage
          message={errors.cgv?.message}
          status={FIELD_STATUS.ERROR}
        />
      </$InputRow>
      <$SubmitButton
        type={BUTTON_TYPES.SUBMIT}
        fluid
        primary
        isLoading={isLoading || isDomainNameLoading}
        isError={!!Object.keys(errors).length}
      >
        <SafeFormattedMessageWithoutSpread message={messages.trialStart} />
      </$SubmitButton>
    </$Form>
  );
};

OnboardingForm.displayName = 'OnboardingForm';

export default OnboardingForm;
